import React, { FC, useCallback, useState } from 'react';
import { NavLink, Route, RouteComponentProps, Switch } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button, Nav } from 'reactstrap';
import { DealerConfig } from '../../../shared/dealer';
import { LinkButton, PageHeader, UserStateComponent } from '../../components';
import {
  ConfigBooleanSetting,
  ConfigNumberBoxSetting,
  ConfigTextBoxSetting,
} from '../../containers/housekeeping/settings/AbstractLiveSettings';
import { TermsElement } from '../../containers/housekeeping/settings/Terms';
import { useTranslation } from '../../translations';
import { Fetcher, useFetcher } from '../../utils';
import { captureError } from '../../utils/errorHandling';

export const DealerSettingsPage: FC<RouteComponentProps> = ({ match: { path, url } }) => {
  const { ts } = useTranslation();
  return (
    <UserStateComponent>
      <PageHeader>{ts('dealer_settings')}</PageHeader>
      <Nav tabs>
        <NavLink activeClassName="active" className="nav-link" exact to={url}>
          {ts('general')}
        </NavLink>
        <NavLink activeClassName="active" className="nav-link" to={`${url}/terms`}>
          {ts('terms')}
        </NavLink>
      </Nav>
      <div style={{ marginTop: '20px' }}>
        <Switch>
          <Route component={DealerSettingsGeneral} exact path={path} />
          <Route component={DealerTermsSettings} exact path={`${path}/terms`} />
        </Switch>
      </div>
    </UserStateComponent>
  );
};

const DealerTermsSettings: FC = () => {
  const fetcher = useFetcher(async () => {
    return await api.getPolicies();
  }, []);

  if (!fetcher.success) {
    return <Fetcher result={fetcher} />;
  }

  return (
    <div>
      <TermsElement
        description="policy_to_be_displayed_to"
        displayName="common.vendor_policy"
        name="vendor"
        policies={fetcher.data}
        refresh={fetcher.refresh}
      />
    </div>
  );
};

const DealerSettingsGeneral: FC = () => {
  const fetcher = useFetcher(async () => {
    return await api.getDealerConfig();
  });

  if (!fetcher.success) {
    return <Fetcher result={fetcher} />;
  }

  return <DealerSettingsKitchenSink initialConfig={fetcher.data} />;
};

const DealerSettingsKitchenSink: FC<{ readonly initialConfig: DealerConfig }> = ({
  initialConfig,
}) => {
  const [config, setConfig] = useState(initialConfig);
  const [saving, setSaving] = useState(false);

  const { ts } = useTranslation();
  const save = useCallback(async () => {
    setSaving(true);
    try {
      await api.updateDealerConfig(config);
      toast.success(ts('dealer_settings_have_been_updated'));
    } catch (error) {
      captureError(error as Error);
    }

    setSaving(false);
  }, [config, ts]);

  return (
    <>
      <h3>{ts('general')}</h3>

      <ConfigBooleanSetting
        description="enables_disables_the_dealers"
        displayName="enable_dealers_module"
        name="enableDealers"
        saving={saving}
        update={(val) => {
          setConfig((old) => {
            return { ...old, enableDealers: val };
          });
        }}
        value={config.enableDealers}
      />

      <ConfigBooleanSetting
        description="whether_to_force_dealers_to_register_for_the_convention"
        displayName="force_dealer_registration"
        name="forceDealerRegistration"
        saving={saving}
        update={(val) => {
          setConfig((old) => {
            return { ...old, forceDealerRegistration: val };
          });
        }}
        value={config.forceDealerRegistration}
      />

      <ConfigNumberBoxSetting
        description="the_number_of_dealer_assistants"
        displayName="dealer_assistant_count_limit"
        name="dealerAssistantLimit"
        saving={saving}
        update={(val) => {
          setConfig((old) => {
            return { ...old, dealerAssistantLimit: val! };
          });
        }}
        value={config.dealerAssistantLimit}
      />

      <ConfigTextBoxSetting
        description="contact_for_dealer_applications_this"
        displayName="dealers_email"
        name="dealers"
        placeholder="dealers"
        saving={saving}
        update={(val) => {
          setConfig((old) => {
            return { ...old, dealerEmail: val! };
          });
        }}
        value={config.dealerEmail}
      />

      <Button
        className="float-end"
        color="primary"
        disabled={saving}
        id="saveSettings"
        onClick={save}
      >
        {ts('common.save')}
      </Button>

      <div className="clearfix">
        <h3>{ts('form_editor')}</h3>
        <div className="start-align mt-10">
          <div>
            {ts(
              'edit_the_dealer_application_form_to_add_custom_text_addremove_elements_or_add_other_options',
            )}
          </div>
          <div className="float-end">
            <LinkButton id="openDealerFormEditor" to="/housekeeping/editor/dealers">
              {ts('form_editor')} {ts('dealer_form_editor')}
            </LinkButton>
          </div>
        </div>
      </div>
    </>
  );
};
